import {
  DEVICE_ROW_STATE,
  DEVICE_ROW_UNSENT,
} from '@omni/services/app-ui/app-ui-screen-controller';
import { AppUiRecordDoc } from '@omni/interfaces/app-ui/record.interface';
import { FORMATTED } from '@omni/utility/dynamics-row.util';
import { ORDER_SCREEN_ENTITY } from '@omni/data-services/order-capture/order-capture.data.service';
import type { OrderDraftSnapshot } from './order-draft';
import {
  OverlayDataSource,
  OverlayNode,
} from '@omni/interfaces/app-ui/overlay.interface';
import { deviceOrderState } from './order-status';
import { hasUnsavedChanges } from '@omni/data-services/app-ui/app-ui-record.data.service';
import {
  cardAttributes,
  datasetFetchXml,
  fetchEntity,
  rowSchema,
  stateFilter,
} from '@omni/services/app-ui/app-ui-fetch';
import { SalesOrderState } from '@omni/enums/order-capture/sales-order.enum';

/**
 * The `historyPanel` variant that lists the rep's open orders of the account instead of its query:
 * the one name the configuration and go-app share for it.
 */
export const DRAFTS_LIST_VARIANT = 'drafts';

const CLOSED_STATES = [
  SalesOrderState.Submitted,
  SalesOrderState.Canceled,
  SalesOrderState.Fulfilled,
  SalesOrderState.Invoiced,
];

/**
 * A query of the rep's drafts: orders whose own `statecode` conditions let only open ones through.
 * New Order's pull runs the screen's drafts dataset, and a list showing it reads New Order's drafts,
 * so Pending and New Order list the same orders from one query.
 */
export function isDraftsQuery(fetchXml: unknown): boolean {
  if (
    typeof fetchXml !== 'string' ||
    fetchEntity(fetchXml) !== ORDER_SCREEN_ENTITY
  )
    return false;
  const passes = stateFilter(fetchXml);
  return (
    !!passes &&
    passes(SalesOrderState.Active) &&
    !CLOSED_STATES.some((state) => passes(state))
  );
}

/** The screen's first drafts dataset: the one New Order's pull runs. */
export function draftsDataSource(
  sources: readonly OverlayDataSource[] | undefined,
): OverlayDataSource | null {
  return (
    (sources ?? []).find((source) => isDraftsQuery(datasetFetchXml(source))) ??
    null
  );
}

/** The drafts dataset as a list runs it (`{{column}}` tokens, its `orderBy`). */
export function draftsFetchXml(
  sources: readonly OverlayDataSource[] | undefined,
): string | null {
  const source = draftsDataSource(sources);
  return source ? datasetFetchXml(source) : null;
}

/** The rep's open orders of an account, as New Order lists them. */
export interface OrderDraftsSource {
  list(): Promise<AppUiRecordDoc[]>;
  /** Online, adds what the rep saved from another device. */
  refresh(): Promise<void>;
}

export interface OrderCardWords {
  translate(key: string): string;
  formatCurrency(value: number): string;
}

export function orderIdOf(record: AppUiRecordDoc): string | null {
  return (record.state as OrderDraftSnapshot | undefined)?.orderId ?? null;
}

/** A list whose card reads orders: one the device's orders can stand in for. */
export function listsOrders(node: OverlayNode): boolean {
  const entity = rowSchema(node)?.entity;
  return !!node.card && (!entity || entity === ORDER_SCREEN_ENTITY);
}

/**
 * Device orders shaped as the node's card reads a server row: the order number (its summary until
 * the server names it) as title, `statusKey`'s words as badge, when it was saved, its total, and any
 * other column the card reads (a footer's `{{ownerid}}`) from the order's header fields.
 * Null for a card that is not about orders.
 */
export function deviceOrderRows(
  records: readonly AppUiRecordDoc[],
  node: OverlayNode,
  statusKey: (record: AppUiRecordDoc) => string,
  words: OrderCardWords,
): Record<string, unknown>[] | null {
  if (!listsOrders(node)) return null;
  return records.flatMap((record) => {
    const row = deviceOrderRow(record, node, statusKey, words);
    return row ? [row] : [];
  });
}

function deviceOrderRow(
  record: AppUiRecordDoc,
  node: OverlayNode,
  statusKey: (order: AppUiRecordDoc) => string,
  words: OrderCardWords,
): Record<string, unknown> | null {
  const orderId = orderIdOf(record);
  if (!orderId) return null;
  const schema = rowSchema(node);
  const card = node.card ?? {};
  const formatted = (attribute: string) => `${attribute}${FORMATTED}`;
  const net = (record.state as OrderDraftSnapshot).net;
  const row: Record<string, unknown> = {
    [schema?.idAttr ?? 'salesorderid']: orderId,
    [schema?.dateAttr ?? 'createdon']: record.modifiedOn,
    ...mergeKeys(record),
  };
  if (card.title) row[card.title] = record.serverName || record.summary;
  if (card.badge) {
    row[card.badge] = record.syncStatus;
    row[formatted(card.badge)] = words.translate(statusKey(record));
  }
  if (card.value && typeof net === 'number') {
    row[card.value] = net;
    row[formatted(card.value)] = words.formatCurrency(net);
  }
  for (const attribute of cardAttributes(card)) {
    const field = record.values?.[attribute];
    if (attribute in row || !field || field.value === null) continue;
    row[attribute] = field.value;
    row[formatted(attribute)] = field.label || String(field.value);
  }
  return row;
}

/** What a list merging the device's orders with the server's reads of one (see DEVICE_ROW_STATE). */
function mergeKeys(record: AppUiRecordDoc): Record<string, unknown> {
  return {
    [DEVICE_ROW_STATE]: deviceOrderState(record),
    [DEVICE_ROW_UNSENT]: !record.serverId || hasUnsavedChanges(record),
  };
}
